import React, { useCallback } from "react";
import { Priority } from "@services/common/types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Device, DeviceFilters } from "@services/devices/types";
import mappers from "@common/utils/filters/mappers";
import { SLFilterToSearch } from "@common/utils/filters/mappers/common";
import {
  isApiOk,
  isApiUnknown,
  isLinkError,
  isLinkOk,
  isLinkUnknown,
  isLoginError,
  isLoginOk,
  isLoginUnknown,
} from "@common/utils/devices/summary";
import { isApiError } from "@common/utils/devices/summary";

type Unknown = "unknown";

export type DeviceFiltersWithPriority = Omit<
  DeviceFilters,
  "status.reachable" | "status.apiError" | "status.loginError"
> & {
  priority: Priority[];
  "status.reachable"?: boolean | Unknown;
  "status.apiError"?: boolean | Unknown;
  "status.loginError"?: boolean | Unknown;
};

export const getFilterLabel = (
  filters: DeviceFiltersWithPriority,
  key: keyof DeviceFiltersWithPriority
) => {
  if (key === "status.reachable") {
    if (filters[key] === "unknown") return "Other";
    if (filters[key]) return "Link Up";
    return "Link Error";
  }
  let res = key === "status.loginError" ? "Login " : "API ";
  if (filters[key] === "unknown") return res + "Other";
  if (filters[key]) return res + "Error";
  return res + "OK";
};

const parseParams = (params: URLSearchParams): DeviceFiltersWithPriority => {
  const asUnknownOrBoolean = (
    params: URLSearchParams,
    key: string
  ): undefined | Unknown | boolean => {
    const value = params.get(key);
    switch (value) {
      case "true":
        return true;
      case "false":
        return false;
      case "unknown":
        return "unknown";
      default:
        return undefined;
    }
  };
  return {
    ...mappers.Devices.fromSearchParams.toSL(params),
    "status.apiError": asUnknownOrBoolean(params, "status.apiError"),
    "status.reachable": asUnknownOrBoolean(params, "status.reachable"),
    "status.loginError": asUnknownOrBoolean(params, "status.loginError"),
    priority: params.getAll("priority") as Priority[],
  };
};

const filterList: [
  isActive: (filters: DeviceFiltersWithPriority) => boolean,
  filter: (filters: DeviceFiltersWithPriority, device: Device) => boolean
][] = [
  [
    (filters) => filters["status.reachable"] !== undefined,
    (filters, device) => {
      switch (filters["status.reachable"]) {
        case "unknown":
          return isLinkUnknown(device);
        case true:
          return isLinkOk(device);
        case false:
          return isLinkError(device);

        default:
          return false;
      }
    },
  ],
  [
    ({ priority }) => priority.length !== 0,
    ({ priority }, device) =>
      device.ticketPriority !== undefined &&
      priority.includes(device.ticketPriority),
  ],
  [
    (filters) => filters["status.apiError"] !== undefined,
    (filters, device) => {
      switch (filters["status.apiError"]) {
        case "unknown":
          return isApiUnknown(device);
        case true:
          return isApiError(device);
        case false:
          return isApiOk(device);

        default:
          return false;
      }
    },
  ],
  [
    (filters) => filters["status.loginError"] !== undefined,
    (filters, device) => {
      switch (filters["status.loginError"]) {
        case "unknown":
          return isLoginUnknown(device);
        case true:
          return isLoginError(device);
        case false:
          return isLoginOk(device);
        default:
          return false;
      }
    },
  ],
];

export const useDeviceFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();

  const tab = searchParams.get("tab");

  const resetFilters = useCallback(() => {
    const paramsObj: Record<string, string> = tab ? { tab } : {};
    const params = new URLSearchParams(paramsObj);

    setSearchParams(params, { replace: true, state });
  }, [tab, state]);

  const changeFilters = useCallback(
    (newFilters: DeviceFiltersWithPriority) => {
      const params = SLFilterToSearch(newFilters as any);

      if (tab) params.set("tab", tab);

      setSearchParams(params, { replace: true, state });
    },
    [tab, state]
  );

  const setFilters = useCallback(
    (fn: (old: DeviceFiltersWithPriority) => DeviceFiltersWithPriority) => {
      changeFilters(fn(parseParams(searchParams)));
    },
    [searchParams, changeFilters]
  );

  const getMatchesFiltersFn = (filters: DeviceFiltersWithPriority) => {
    const usedFilters = filterList
      .filter(([isActive]) => isActive(filters))
      .map(([_, fn]) => fn);
    return (device: Device) => usedFilters.every((fn) => fn(filters, device));
  };

  const filters = parseParams(searchParams);
  return {
    resetFilters,
    setFilters,
    filters,
    filterFn: getMatchesFiltersFn(filters),
  };
};
